/**
 * The chart a ``chart`` call asked for, drawn from the ledger as it stands.
 *
 * Nothing is kept from when the agent ran: the call holds the question - what
 * to group, over what, filtered how - and this puts that same question to the
 * server whenever the card is drawn. The agent never carried the points, so it
 * cannot have mistyped them, and a chart opened next week is of next week's
 * ledger. The filters are the rules a query is asked with, which is what the
 * service reads them as, so what the agent asked for is asked here untouched.
 */

import { useQuery } from "@apollo/client/react";

import { ChartFormatEnum } from "@/__generated__/graphql";
import { Chart } from "@/components/organisms/Chart";
import { usePendingSession } from "@/components/organisms/PendingSession/PendingSession.store";
import { Queries } from "@/constants";
import type { ToolCallChartSpec } from "@/components/organisms/ToolCallCard/ToolCallCard.tools";
import { useScope } from "@/hooks/useScope";
import { Typography } from "@/components/ui/typography";

export function ToolCallCardChart({ spec }: { spec: ToolCallChartSpec }) {
  const { activeEntityIdentifiers } = useScope();
  const pendingSessionId = usePendingSession()?.pendingSessionId ?? null;

  const { data, loading, error } = useQuery(Queries.SUMMARY_QUERY, {
    variables: {
      sourceName: spec.source as string,
      title: spec.title ?? "",
      groupBy: (spec.group_by ?? []).map((group) => ({
        field: group.field as string,
        interval: group.interval ?? null,
      })),
      aggregate: (spec.aggregate ?? []).map((item) => ({
        op: item.op as string,
        field: item.field ?? null,
      })),
      filters: spec.filters ?? null,
      search: spec.search ?? null,
      orderBy: spec.order_by ?? null,
      entityIdentifiers: activeEntityIdentifiers ?? [],
      pendingSession: pendingSessionId,
    },
    errorPolicy: "all",
  });

  const chart = data?.summary?.data;
  // Why it could not be drawn, in the server's own words - a field the ledger
  // has not got, an entity out of reach. Told only that it failed, there is
  // nothing to do about it and nothing to report.
  const errors = data?.summary?.errors;
  const refused =
    errors?.notFoundError?.message ??
    errors?.authorizationError?.message ??
    errors?.genericErrors?.[0]?.message ??
    (error ? error.message : null);

  if (refused && !chart) {
    return (
      <div className="pl-2 pr-2 pt-1.5 pb-1.5">
        <Typography level="body-xs" className="text-destructive">
          This chart could not be drawn: {refused}
        </Typography>
      </div>
    );
  }

  return (
    <div className="pl-2 pr-2 pt-1.5 pb-1.5">
      <Chart
        data={
          chart ?? {
            title: spec.title ?? "",
            series: [],
            points: [],
            labelFormat: ChartFormatEnum.String,
            valueFormat: ChartFormatEnum.Currency,
            labelAxis: "",
            valueAxis: "",
          }
        }
        type="bar"
        size="sm"
        // The card's own header says what the chart is of.
        hideTitle
        loading={loading && !chart}
      />
    </div>
  );
}
